import type { FC, ReactNode } from 'react'

import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ORDER_STEPS, findOrderStatus } from '@/lib/orders/status'

interface OrderProgressProps {
    status: string
    depositPaid: boolean
    compact?: boolean
    dark?: boolean
}

const OrderProgress: FC<OrderProgressProps> = ({ status, depositPaid, compact = false, dark = false }): ReactNode => {
    const option = findOrderStatus(status)

    if (option.id === 'geannuleerd') {
        return (
            <p className='rounded-md bg-red-400/10 px-4 py-3 text-sm text-red-700 dark:text-red-300 ring-1 ring-red-400/30'>
                Deze bestelling is geannuleerd.
            </p>
        )
    }

    const current = Math.max(option.step, depositPaid ? 1 : 0)

    return (
        <ol className={cn('grid grid-cols-6 gap-1', compact ? 'text-[10px]' : 'text-[11px] sm:text-xs')} aria-label='Voortgang van de bestelling'>
            {ORDER_STEPS.map(step => {
                const done = step.step < current || (step.step === current && option.id === 'geplaatst')
                const active = step.step === current && option.id !== 'geplaatst'

                return (
                    <li key={step.step} className='flex flex-col gap-2' aria-current={active ? 'step' : undefined}>
                        <span className={cn('relative h-1.5 overflow-hidden rounded-full', dark ? 'bg-white/15' : 'bg-border')}>
                            <span
                                className={cn(
                                    'absolute inset-y-0 left-0 rounded-full transition-all duration-700',
                                    done ? cn('w-full', dark ? 'bg-ink-foreground' : 'bg-foreground') : active ? 'w-1/2 bg-brass' : 'w-0'
                                )}
                            />
                        </span>

                        <span
                            className={cn(
                                'flex items-center gap-1 font-medium',
                                done || active ? (dark ? 'text-ink-foreground' : 'text-foreground') : dark ? 'text-ink-foreground/45' : 'text-muted-foreground'
                            )}
                        >
                            {done && <Check className='size-3 shrink-0 text-brass' aria-hidden='true' />}
                            <span className='truncate'>{step.label}</span>
                        </span>
                    </li>
                )
            })}
        </ol>
    )
}

export default OrderProgress
